import React, { useMemo } from "react";
import { Box, Grid, Tooltip, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Gauge from "react-gauge-chart";
import { Product } from "../../interfaces/DTO/mislaka_xml_models";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import { MimshakService } from "../../services/mimshakService";
import { formatCurrency } from "../../utils/namesFormater";
import { fundService } from "../../services/fundService";

const IRA_SUB_SPECIALIZATION = "בניהול אישי IRA";

interface ExcludedPathInfo {
  fundName?: string;
  policyNumber?: string;
  amount: number;
  reason: "IRA" | "missing_data";
}

interface ExposureGaugesByProductProps {
  products: Product[];
  fundData: FundDataBasic[];
  refreshTrigger?: number;
}

export const ExposureGaugesByProduct: React.FC<ExposureGaugesByProductProps> = ({
  products,
  fundData,
  refreshTrigger: _refreshTrigger,
}) => {
  const { totalAmount, exposures, totalExcludedAmount, excludedPaths } = useMemo(() => {
    const policies = products.flatMap(p => p.policies_or_accounts || []);
    const allPaths = policies.flatMap(policy =>
      (policy.investment_paths || []).map(path => ({ path, policyNumber: policy.policy_number }))
    );

    const fundMap = new Map<number, any>();
    for (const { path } of allPaths) {
      if (path.path_code !== undefined && !fundMap.has(path.path_code)) {
        const fund = fundService.getFundFromCacheById(path.path_code);
        if (fund) fundMap.set(path.path_code, fund);
      }
    }

    let totalAccumulatedAmount = 0;
    let totalStockExposureAmount = 0;
    let totalCurrencyExposureAmount = 0;
    let totalForeignExposureAmount = 0;
    let weightedStockExposure = 0;
    let weightedCurrencyExposure = 0;
    let weightedForeignExposure = 0;
    let totalExcluded = 0;
    const excludedPaths: ExcludedPathInfo[] = [];

    for (const { path, policyNumber } of allPaths) {
      const fund = fundMap.get(path.path_code);
      const amount = path.accumulated_amount ?? 0;
      if (amount <= 0) continue;

      if (!fund) {
        totalExcluded += amount;
        excludedPaths.push({ fundName: path.path_name, policyNumber, amount, reason: "missing_data" });
        continue;
      }
      if (fund.sub_specialization === IRA_SUB_SPECIALIZATION) {
        totalExcluded += amount;
        excludedPaths.push({ fundName: fund.fund_name ?? path.path_name, policyNumber, amount, reason: "IRA" });
        continue;
      }

      if (fund.total_assets) {
        totalAccumulatedAmount += amount;
        if (fund.stock_market_exposure !== undefined) {
          const stock = fund.stock_market_exposure / fund.total_assets;
          totalStockExposureAmount += stock * amount;
          weightedStockExposure += stock * amount;
        }
        if (fund.foreign_currency_exposure !== undefined) {
          const currency = fund.foreign_currency_exposure / fund.total_assets;
          totalCurrencyExposureAmount += currency * amount;
          weightedCurrencyExposure += currency * amount;
        }
        if (fund.foreign_exposure !== undefined) {
          const foreign = fund.foreign_exposure / fund.total_assets;
          totalForeignExposureAmount += foreign * amount;
          weightedForeignExposure += foreign * amount;
        }
      } else {
        totalExcluded += amount;
        excludedPaths.push({ fundName: fund.fund_name ?? path.path_name, policyNumber, amount, reason: "missing_data" });
      }
    }

    return {
      totalAmount: MimshakService.calculateTotals(policies),
      exposures: {
        stockExposure: { average: totalAccumulatedAmount ? weightedStockExposure / totalAccumulatedAmount : 0, amount: totalStockExposureAmount },
        currencyExposure: { average: totalAccumulatedAmount ? weightedCurrencyExposure / totalAccumulatedAmount : 0, amount: totalCurrencyExposureAmount },
        foreignExposure: { average: totalAccumulatedAmount ? weightedForeignExposure / totalAccumulatedAmount : 0, amount: totalForeignExposureAmount },
      },
      totalExcludedAmount: totalExcluded,
      excludedPaths,
    };
  }, [products, fundData]);

  const buildExcludedTooltip = (): React.ReactNode => {
    if (excludedPaths.length === 0) return null;
    const iraItems = excludedPaths.filter(e => e.reason === "IRA");
    const missingItems = excludedPaths.filter(e => e.reason === "missing_data");
    const lines: string[] = [`סכום כולל מוחרג: ${formatCurrency(totalExcludedAmount)}\n`];
    if (iraItems.length > 0) {
      lines.push("מסלולי IRA (בניהול אישי) - מוחרגים מהחישוב:");
      iraItems.forEach(e => lines.push(`  • ${e.fundName ?? "לא ידוע"}${e.policyNumber ? ` (פוליסה ${e.policyNumber})` : ""} — ${formatCurrency(e.amount)}`));
    }
    if (missingItems.length > 0) {
      if (iraItems.length > 0) lines.push("");
      lines.push("מסלולים ללא נתוני חשיפה:");
      missingItems.forEach(e => lines.push(`  • ${e.fundName ?? "לא ידוע"}${e.policyNumber ? ` (פוליסה ${e.policyNumber})` : ""} — ${formatCurrency(e.amount)}`));
    }
    return <span style={{ whiteSpace: "pre-line" }}>{lines.join("\n")}</span>;
  };

  const renderGauge = (
    id: string,
    exposure: { average: number; amount: number },
    label: string
  ) => (
    <Box textAlign="center">
      <Gauge
        id={id}
        nrOfLevels={100}
        percent={parseFloat(exposure.average.toFixed(3))}
        textColor="#000"
      />
      <Box mt={1} display="flex" alignItems="center" justifyContent="center">
        <strong>{label}</strong>
        {totalExcludedAmount > 0 && (
          <Tooltip title={buildExcludedTooltip()} arrow>
            <WarningAmberIcon color="warning" sx={{ ml: 1 }} />
          </Tooltip>
        )}
      </Box>
      <div>{formatCurrency(exposure.amount)}</div>
    </Box>
  );

  return (
    <>
      <Box textAlign="right" mb={0} mr={2}>
        <Typography variant="h5">
          סך הכל: {formatCurrency(totalAmount)}
          {totalExcludedAmount > 0 && (
            <span style={{ color: "gray", marginRight: 25 }}>
              הוצא מהחישוב:
              <WarningAmberIcon color="warning" sx={{ ml: 0.5 }} />
              ({formatCurrency(totalExcludedAmount)})
            </span>
          )}
        </Typography>
      </Box>
      <Grid container spacing={0} justifyContent="center" dir="rtl">
        <Grid item xs={12} md={4}>
          {renderGauge("stock-exposure-gauge", exposures.stockExposure, "חשיפה למניות")}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderGauge("currency-exposure-gauge", exposures.currencyExposure, 'חשיפה למט"ח')}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderGauge("foreign-exposure-gauge", exposures.foreignExposure, 'חשיפה לחו"ל')}
        </Grid>
      </Grid>

    </>
  );
};